import { UserDTOInternalRolesEnum } from '@reposit/api-client';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { openEvidenceWorkspaceThunk } from '../../redux/claim/claim.thunk';
import { AppState } from '../../redux/root.reducer';
import IdentifierValue from '../IdentifierValue';
import { Table, Td } from '../Table';

// Shared table cells only pad the first and last column, so a new last column
// leaves Type, the upload time, and Action flush against each other.
export const DocumentTable = styled(Table)`
  && td {
    overflow-wrap: break-word;
    padding-left: 16px;
    padding-right: 16px;
    white-space: normal;
  }

  && td:first-child {
    padding-left: 40px;
  }

  && td:last-child {
    padding-right: 20px;
  }
`;

export const DocumentIdCell: React.FC = ({ children }) => (
  <Td>
    <IdentifierValue>{children}</IdentifierValue>
  </Td>
);

export function documentTypeLabel(typeId: string): string {
  return typeId
    .toLowerCase()
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primaryBrand};
  cursor: pointer;
  display: inline;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;
  white-space: normal;
`;

interface EvidenceWorkspaceDocumentLinkProps {
  documentId: string;
  documentName: string;
}

export function useCanOpenEvidenceWorkspace(): boolean {
  const { id: claimId } = useParams<{ id: string }>();
  const enabled = useSelector((state: AppState) => hasInternalRole(state, UserDTOInternalRolesEnum.EVIDENCEWORKSPACE));
  return Boolean(enabled && claimId);
}

export const EvidenceWorkspaceDocumentLink = ({ documentId, documentName }: EvidenceWorkspaceDocumentLinkProps) => {
  const { id: claimId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const enabled = useCanOpenEvidenceWorkspace();

  if (!enabled || !claimId) {
    return null;
  }

  return (
    <LinkButton
      type="button"
      aria-label={`Show ${documentName} in the evidence workspace`}
      onClick={() => {
        dispatch(openEvidenceWorkspaceThunk({ claimId, documentId }));
      }}
    >
      Show
    </LinkButton>
  );
};
